import { useTranslation } from 'react-i18next';

const fmtNumber = (n) => (n || 0).toLocaleString();

const fmtHoursTotal = (secs) => {
  if (!secs) return '0s total';
  if (secs < 60) return `${secs}s total`;
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s ? `${m}m ${s}s total` : `${m}m total`;
  }
  return (secs / 3600).toFixed(1) + ' hours total';
};

const fmtSeconds = (secs) => {
  if (!secs) return '0s';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};

const KpiCard = ({ value, label, secondary, description, loading }) => (
  <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 flex flex-col items-center text-center">
    {loading ? (
      <div className="animate-pulse flex flex-col gap-3">
        <div className="h-8 w-28 bg-[#E2E8F0] rounded" />
        <div className="h-4 w-44 bg-[#E2E8F0] rounded" />
        <div className="h-3 w-36 bg-[#E2E8F0] rounded mt-1" />
        <div className="h-px w-full bg-[#E2E8F0] rounded mt-auto" />
        <div className="h-3 w-48 bg-[#E2E8F0] rounded" />
      </div>
    ) : (
      <>
        <div className="flex flex-col gap-1 flex-1 w-full">
          <p className="text-[30px] font-bold text-[#0F172A] leading-tight tracking-tight">
            {value}
          </p>
          <p className="text-[14px] font-semibold text-[#334155] mt-1">{label}</p>
          {secondary && (
            <p className="text-[13px] text-[#64748B]">{secondary}</p>
          )}
        </div>
        {description && (
          <>
            <div className="border-t border-[#E2E8F0] mt-4 w-full" />
            <p className="text-[12px] text-[#94A3B8] mt-3 leading-relaxed">{description}</p>
          </>
        )}
      </>
    )}
  </div>
);

const EMPTY = '—';

const KpiCards = ({ summary, loading }) => {
  const { t } = useTranslation();
  const viewers = summary?.uniqueViewers || 0;
  const hasData = !!summary;

  const avgViewingTime =
    hasData && viewers > 0
      ? fmtSeconds(Math.round((summary.totalActiveViewingSeconds || 0) / viewers)) + ` ${t('admin.liveStatsEngagement.avgPerFan')}`
      : null;

  const avgReturns =
    hasData && viewers > 0
      ? ((summary.totalReturnCount || 0) / viewers).toFixed(1) + ` ${t('admin.liveStatsEngagement.avgReturnsPerFan')}`
      : null;

  return (
    <div className="grid grid-cols-3 gap-5 p-6">
      <KpiCard
        loading={loading}
        value={hasData ? fmtNumber(summary.totalUpdatesConsumed) : EMPTY}
        label={t('admin.liveStatsEngagement.liveUpdateImpressions')}
        description={t('admin.liveStatsEngagement.liveUpdateImpressionsDesc')}
      />
      <KpiCard
        loading={loading}
        value={hasData ? fmtHoursTotal(summary.totalActiveViewingSeconds) : EMPTY}
        label={t('admin.liveStatsEngagement.activeViewingTime')}
        secondary={avgViewingTime}
        description={t('admin.liveStatsEngagement.activeViewingTimeDesc')}
      />
      <KpiCard
        loading={loading}
        value={hasData ? fmtNumber(summary.totalReturnCount) : EMPTY}
        label={t('admin.liveStatsEngagement.returnFrequency')}
        secondary={avgReturns}
        description={t('admin.liveStatsEngagement.returnFrequencyDesc')}
      />
    </div>
  );
};

export default KpiCards;
